/**
 * ESRS G1 — Business Conduct
 *
 * Board composition & diversity register (ESRS 2 GOV-1) and political
 * influence / lobbying activities (G1-5). The GOV-1 gender split feeds
 * SFDR PAI 13 (board gender diversity ratio). Board members are
 * entity-scoped and never deleted — deactivation preserves the register
 * history for assurance. Lobbying and the disclosure are entity+period
 * scoped.
 *
 * Backend contract (backend/routes/g1_routes.py):
 *   GET  /api/esrs/g1/board-members?entity_id=&include_inactive=  → { members: [...] }
 *   POST /api/esrs/g1/board-members                               → member
 *   POST /api/esrs/g1/board-members/{member_id}/deactivate        → deactivated member
 *   GET  /api/esrs/g1/lobbying?entity_id=&reporting_period=       → { activities: [...] }
 *   POST /api/esrs/g1/lobbying                                    → activity
 *   GET  /api/esrs/g1/disclosure?entity_id=&reporting_period=
 *        → { esrs2_gov1: { board_size, female_pct, female_to_male_ratio_pct, ... },
 *            esrs_g1_5: { activities_count, total_amount_eur, by_policy_area_eur } }
 */
import { useCallback, useEffect, useState } from "react";
import {
  Landmark, Gauge, Plus, X, Users, User, ShieldCheck, ShieldOff,
  CheckCircle2, CircleOff, Megaphone, Eye, EyeOff, UserX, PieChart,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  TabBar, EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt,
  C, MONO, SANS,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

const GENDER_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
  { value: "undisclosed", label: "Undisclosed" },
];

const ROLE_OPTIONS = [
  { value: "chair", label: "Chair" },
  { value: "executive", label: "Executive" },
  { value: "non_executive", label: "Non-executive" },
  { value: "employee_representative", label: "Employee rep" },
  { value: "other", label: "Other" },
];

const roleLabel = (role) =>
  ROLE_OPTIONS.find((o) => o.value === role)?.label || role || "—";

// Gender is a register attribute, not a status — neutral tones only.
function genderBadge(gender) {
  const label = GENDER_OPTIONS.find((o) => o.value === gender)?.label || gender || "—";
  return <Badge c={C.neutral} Icon={User} label={label} />;
}

const EMPTY_MEMBER_FORM = {
  name: "",
  gender: "female",
  role: "non_executive",
  independent: false,
  esg_competencies: "",
};

const EMPTY_ACTIVITY_FORM = {
  recipient: "",
  policy_area: "",
  jurisdiction: "",
  amount_eur: "",
  description: "",
};

export default function G1Conduct() {
  const { entities, entityId, setEntityId } = useEntities("esrs_g1_entity_id");
  const [period, setPeriod] = useState("FY2026");
  const [periodDraft, setPeriodDraft] = useState("FY2026");

  const [tab, setTab] = useState("board");
  const [disclosure, setDisclosure] = useState(null);
  const [members, setMembers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showMemberForm, setShowMemberForm] = useState(false);
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER_FORM);
  const [savingMember, setSavingMember] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState("");

  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY_FORM);
  const [savingActivity, setSavingActivity] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    const eq = `entity_id=${encodeURIComponent(entityId)}`;
    const pq = `${eq}&reporting_period=${encodeURIComponent(period)}`;
    try {
      const [disc, board, lobby] = await Promise.all([
        apiFetch(`/api/esrs/g1/disclosure?${pq}`),
        apiFetch(`/api/esrs/g1/board-members?${eq}&include_inactive=${includeInactive}`),
        apiFetch(`/api/esrs/g1/lobbying?${pq}`),
      ]);
      setDisclosure(disc || null);
      setMembers(board?.members || []);
      setActivities(lobby?.activities || []);
    } catch (e) {
      setError(e.message || "Failed to load business conduct data");
      setDisclosure(null);
      setMembers([]);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, period, includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const setMemberField = (k, v) => setMemberForm((f) => ({ ...f, [k]: v }));
  const setActivityField = (k, v) => setActivityForm((f) => ({ ...f, [k]: v }));

  const submitMember = async (e) => {
    e.preventDefault();
    if (!entityId) return;
    setSavingMember(true);
    setError("");
    try {
      await apiFetch("/api/esrs/g1/board-members", {
        method: "POST",
        body: {
          entity_id: entityId,
          name: memberForm.name.trim(),
          gender: memberForm.gender,
          role: memberForm.role,
          independent: memberForm.independent,
          esg_competencies: memberForm.esg_competencies
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        },
      });
      setMemberForm(EMPTY_MEMBER_FORM);
      setShowMemberForm(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to add board member");
    } finally {
      setSavingMember(false);
    }
  };

  const deactivateMember = async (memberId) => {
    setDeactivatingId(memberId);
    setError("");
    try {
      await apiFetch(`/api/esrs/g1/board-members/${memberId}/deactivate`, {
        method: "POST",
      });
      await load();
    } catch (err) {
      setError(err.message || "Failed to deactivate board member");
    } finally {
      setDeactivatingId("");
    }
  };

  const submitActivity = async (e) => {
    e.preventDefault();
    if (!entityId) return;
    setSavingActivity(true);
    setError("");
    try {
      await apiFetch("/api/esrs/g1/lobbying", {
        method: "POST",
        body: {
          entity_id: entityId,
          reporting_period: period,
          recipient: activityForm.recipient.trim(),
          policy_area: activityForm.policy_area.trim(),
          jurisdiction: activityForm.jurisdiction.trim() || undefined,
          amount_eur: Number(activityForm.amount_eur || 0),
          description: activityForm.description.trim() || undefined,
        },
      });
      setActivityForm(EMPTY_ACTIVITY_FORM);
      setShowActivityForm(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to record lobbying activity");
    } finally {
      setSavingActivity(false);
    }
  };

  const periodInput = (
    <input
      value={periodDraft}
      onChange={(e) => setPeriodDraft(e.target.value)}
      onBlur={() => setPeriod(periodDraft)}
      onKeyDown={(e) => { if (e.key === "Enter") setPeriod(periodDraft); }}
      aria-label="Reporting period"
      placeholder="FY2026"
      style={{ ...field, width: 110, textAlign: "center" }}
    />
  );

  const header = (
    <PageHeader
      overline="ESRS G1 · Business Conduct"
      OverlineIcon={Landmark}
      title="Business Conduct"
      subtitle="Board composition & diversity register (ESRS 2 GOV-1) and political influence & lobbying activities (G1-5). The board gender split feeds the SFDR PAI 13 board gender diversity ratio."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={load}
      loading={loading}
      actions={periodInput}
    />
  );

  if (!entityId) {
    return (
      <Page>
        {header}
        <Card>
          <EmptyState
            Icon={Landmark}
            title="No reporting entity selected"
            body="Choose a reporting entity to view its board composition register, lobbying activities and the ESRS G1 / GOV-1 disclosure."
          />
        </Card>
      </Page>
    );
  }

  const g = disclosure?.esrs2_gov1 || {};
  const l5 = disclosure?.esrs_g1_5 || {};
  const ratioNull = g.female_to_male_ratio_pct === null || g.female_to_male_ratio_pct === undefined;
  const independentPct =
    g.board_size > 0 && g.independent_members !== undefined
      ? (g.independent_members / g.board_size) * 100
      : null;

  const kpis = [
    { label: "Board size", value: fmt(g.board_size, 0) },
    { label: "Female members", value: fmt(g.female_pct, 1), unit: "%", color: C.primary },
    {
      label: "Female-to-male ratio (PAI 13)",
      value: fmt(g.female_to_male_ratio_pct, 1),
      unit: ratioNull ? undefined : "%",
      color: ratioNull ? C.muted : C.locked,
    },
    { label: "Independent members", value: fmt(g.independent_members, 0) },
    { label: "ESG-competent", value: fmt(g.esg_competent_pct, 1), unit: "%", color: C.primary },
    { label: "Lobbying spend", value: fmt(l5.total_amount_eur), unit: "EUR", color: C.accent },
    { label: "Lobbying activities", value: fmt(l5.activities_count, 0) },
  ];

  const memberColumns = [
    {
      key: "name",
      label: "Name",
      render: (r) => <Data>{r.name || "—"}</Data>,
    },
    {
      key: "gender",
      label: "Gender",
      render: (r) => genderBadge(r.gender),
    },
    {
      key: "role",
      label: "Role",
      render: (r) => <Data color={C.body}>{roleLabel(r.role)}</Data>,
    },
    {
      key: "independent",
      label: "Independence",
      render: (r) =>
        r.independent ? (
          <Badge c={C.primary} Icon={ShieldCheck} label="Independent" />
        ) : (
          <Badge c={C.neutral} Icon={ShieldOff} label="Not independent" />
        ),
    },
    {
      key: "esg_competencies",
      label: "ESG competencies",
      render: (r) =>
        (r.esg_competencies || []).length === 0 ? (
          <Data color={C.muted}>—</Data>
        ) : (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {r.esg_competencies.map((comp) => (
              <span
                key={comp}
                style={{
                  fontFamily: MONO, fontSize: 10, fontWeight: 600, color: C.body,
                  background: C.surfaceHi, border: `1px solid ${C.border}`,
                  borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap",
                }}
              >
                {comp}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: "active",
      label: "Status",
      render: (r) =>
        r.active ? (
          <Badge c={C.primary} Icon={CheckCircle2} label="Active" />
        ) : (
          <Badge c={C.neutral} Icon={CircleOff} label="Inactive" />
        ),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (r) =>
        r.active ? (
          <Btn
            variant="destructive"
            Icon={UserX}
            onClick={() => deactivateMember(r.member_id)}
            disabled={deactivatingId === r.member_id}
            title="Deactivate — the register keeps history"
          >
            {deactivatingId === r.member_id ? "Deactivating…" : "Deactivate"}
          </Btn>
        ) : null,
    },
  ];

  const activityColumns = [
    {
      key: "recipient",
      label: "Recipient",
      render: (r) => <Data>{r.recipient || "—"}</Data>,
    },
    {
      key: "policy_area",
      label: "Policy area",
      render: (r) => <Data color={C.body}>{r.policy_area || "—"}</Data>,
    },
    {
      key: "jurisdiction",
      label: "Jurisdiction",
      render: (r) => <Data color={C.body}>{r.jurisdiction || "—"}</Data>,
    },
    {
      key: "amount_eur",
      label: "Amount (EUR)",
      align: "right",
      render: (r) => <Data color={C.ink}>{fmt(r.amount_eur)}</Data>,
    },
    {
      key: "description",
      label: "Description",
      render: (r) => (
        <span
          title={r.description || undefined}
          style={{
            fontFamily: SANS, fontSize: 12, color: C.body, display: "inline-block",
            maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            verticalAlign: "middle",
          }}
        >
          {r.description || "—"}
        </span>
      ),
    },
  ];

  const policyAreaRows = Object.entries(l5.by_policy_area_eur || {}).sort(
    (a, b) => b[1] - a[1]
  );

  return (
    <Page>
      {header}
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading && !disclosure ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={110} />
          <Skeleton h={200} />
          <Skeleton h={200} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {/* KPI strip */}
          <Card>
            <Overline Icon={Gauge}>G1 disclosure · {period}</Overline>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 20,
              }}
            >
              {kpis.map((k) => (
                <Metric key={k.label} label={k.label} value={k.value} unit={k.unit} color={k.color} />
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
                alignItems: "center",
                fontFamily: SANS,
                fontSize: 12,
                color: C.body,
              }}
            >
              <span>
                Female <Data color={C.ink}>{fmt(g.female_members, 0)}</Data>
                {" · "}Male <Data color={C.ink}>{fmt(g.male_members, 0)}</Data>
              </span>
              <span>
                Independent share{" "}
                <Data color={C.ink}>
                  {independentPct === null ? "—" : `${fmt(independentPct, 1)}%`}
                </Data>
              </span>
              <span>
                ESG-competent members <Data color={C.ink}>{fmt(g.esg_competent_members, 0)}</Data>
              </span>
              {ratioNull && (g.board_size || 0) > 0 && (
                <span style={{ color: C.muted }}>
                  Female-to-male ratio unavailable — no male members
                </span>
              )}
            </div>
          </Card>

          <TabBar
            tabs={[
              { id: "board", label: "Board register", count: members.length },
              { id: "lobbying", label: "Lobbying · G1-5", count: activities.length },
            ]}
            active={tab}
            onChange={setTab}
          />

          {tab === "board" && (
            <>
              {/* Board register (ESRS 2 GOV-1) */}
              <Card>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <Overline Icon={Users} color={C.body} style={{ marginBottom: 0 }}>
                    Board register · GOV-1
                  </Overline>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Btn
                      variant="ghost"
                      Icon={includeInactive ? EyeOff : Eye}
                      onClick={() => setIncludeInactive((v) => !v)}
                    >
                      {includeInactive ? "Hide inactive" : "Show inactive"}
                    </Btn>
                    <Btn
                      variant={showMemberForm ? "ghost" : "secondary"}
                      Icon={showMemberForm ? X : Plus}
                      onClick={() => setShowMemberForm((s) => !s)}
                    >
                      {showMemberForm ? "Cancel" : "Add member"}
                    </Btn>
                  </div>
                </div>

                {showMemberForm && (
                  <form onSubmit={submitMember} style={{ marginTop: 16 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: 14,
                      }}
                    >
                      <div>
                        <Label>Name *</Label>
                        <input
                          value={memberForm.name}
                          onChange={(e) => setMemberField("name", e.target.value)}
                          required
                          placeholder="Astrid Lindqvist"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Gender *</Label>
                        <select
                          value={memberForm.gender}
                          onChange={(e) => setMemberField("gender", e.target.value)}
                          required
                          style={field}
                        >
                          {GENDER_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Role *</Label>
                        <select
                          value={memberForm.role}
                          onChange={(e) => setMemberField("role", e.target.value)}
                          required
                          style={field}
                        >
                          {ROLE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>ESG competencies</Label>
                        <input
                          value={memberForm.esg_competencies}
                          onChange={(e) => setMemberField("esg_competencies", e.target.value)}
                          placeholder="climate, social, governance"
                          style={field}
                        />
                        <p style={{ fontFamily: SANS, fontSize: 11, color: C.muted, margin: "4px 0 0" }}>
                          Comma-separated — feeds the GOV-1 ESG-competence share.
                        </p>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", paddingTop: 18 }}>
                        <label
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 8,
                            fontFamily: SANS, fontSize: 13, color: C.ink, cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={memberForm.independent}
                            onChange={(e) => setMemberField("independent", e.target.checked)}
                            style={{ accentColor: C.primary, width: 15, height: 15 }}
                          />
                          Independent member
                        </label>
                      </div>
                    </div>
                    <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                      <Btn type="submit" Icon={Plus} disabled={savingMember || !memberForm.name.trim()}>
                        {savingMember ? "Saving…" : "Add to register"}
                      </Btn>
                      <Btn variant="secondary" onClick={() => { setMemberForm(EMPTY_MEMBER_FORM); setShowMemberForm(false); }}>
                        Reset
                      </Btn>
                    </div>
                  </form>
                )}

                <div style={{ marginTop: 16 }}>
                  {members.length === 0 ? (
                    <EmptyState
                      Icon={Users}
                      title="No board members registered"
                      body="Register the members of the administrative, management and supervisory bodies to build the ESRS 2 GOV-1 composition disclosure and the SFDR PAI 13 feed."
                    />
                  ) : (
                    <DataTable
                      columns={memberColumns}
                      rows={members}
                      keyField="member_id"
                      emptyLabel="No board members"
                    />
                  )}
                </div>
                <p style={{ fontFamily: SANS, fontSize: 11, color: C.muted, margin: "12px 0 0" }}>
                  Members are deactivated, never deleted — the register keeps history for assurance.
                </p>
              </Card>
            </>
          )}

          {tab === "lobbying" && (
            <>
              {/* Lobbying activities (G1-5) */}
              <Card>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <Overline Icon={Megaphone} color={C.body} style={{ marginBottom: 0 }}>
                    Political influence & lobbying · G1-5
                  </Overline>
                  <Btn
                    variant={showActivityForm ? "ghost" : "secondary"}
                    Icon={showActivityForm ? X : Plus}
                    onClick={() => setShowActivityForm((s) => !s)}
                  >
                    {showActivityForm ? "Cancel" : "Add activity"}
                  </Btn>
                </div>

                {showActivityForm && (
                  <form onSubmit={submitActivity} style={{ marginTop: 16 }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: 14,
                      }}
                    >
                      <div>
                        <Label>Recipient *</Label>
                        <input
                          value={activityForm.recipient}
                          onChange={(e) => setActivityField("recipient", e.target.value)}
                          required
                          placeholder="EU Parliament"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Policy area *</Label>
                        <input
                          value={activityForm.policy_area}
                          onChange={(e) => setActivityField("policy_area", e.target.value)}
                          required
                          placeholder="Climate"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Jurisdiction</Label>
                        <input
                          value={activityForm.jurisdiction}
                          onChange={(e) => setActivityField("jurisdiction", e.target.value)}
                          placeholder="EU"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Amount (EUR) *</Label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={activityForm.amount_eur}
                          onChange={(e) => setActivityField("amount_eur", e.target.value)}
                          required
                          placeholder="0"
                          style={field}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <input
                          value={activityForm.description}
                          onChange={(e) => setActivityField("description", e.target.value)}
                          placeholder="Position paper on CBAM scope extension"
                          style={field}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                      <Btn
                        type="submit"
                        Icon={Plus}
                        disabled={
                          savingActivity ||
                          !activityForm.recipient.trim() ||
                          !activityForm.policy_area.trim() ||
                          activityForm.amount_eur === ""
                        }
                      >
                        {savingActivity ? "Saving…" : "Record activity"}
                      </Btn>
                      <Btn variant="secondary" onClick={() => { setActivityForm(EMPTY_ACTIVITY_FORM); setShowActivityForm(false); }}>
                        Reset
                      </Btn>
                    </div>
                  </form>
                )}

                <div style={{ marginTop: 16 }}>
                  {activities.length === 0 ? (
                    <EmptyState
                      Icon={Megaphone}
                      title="No lobbying activities recorded"
                      body="Record political contributions and lobbying activities — recipient, policy area and amount — to build the ESRS G1-5 political influence disclosure for this period."
                    />
                  ) : (
                    <DataTable
                      columns={activityColumns}
                      rows={activities}
                      keyField="activity_id"
                      emptyLabel="No lobbying activities"
                    />
                  )}
                </div>
              </Card>

              {/* Spend by policy area (from the G1-5 disclosure) */}
              <Card>
                <Overline Icon={PieChart} color={C.body}>
                  Spend by policy area
                </Overline>
                {policyAreaRows.length === 0 ? (
                  <EmptyState
                    Icon={PieChart}
                    title="No spend to break down"
                    body="Once lobbying activities are recorded, the G1-5 disclosure aggregates total spend per policy area here."
                  />
                ) : (
                  <div style={{ display: "grid", gap: 8, maxWidth: 480 }}>
                    {policyAreaRows.map(([area, amount]) => (
                      <div
                        key={area}
                        style={{
                          display: "flex", justifyContent: "space-between", gap: 12,
                          borderBottom: `1px solid ${C.border}`, paddingBottom: 6,
                        }}
                      >
                        <Data color={C.body}>{area}</Data>
                        <Data color={C.ink}>{fmt(amount)} EUR</Data>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      )}
    </Page>
  );
}
